import type { DerivedStats, DieSides, GameState, MasterDie, Rarity } from '../../../core/types';
import { dieScore } from '../../../core/dice';
import { recycleValue } from '../../../core/gacha';
import { RARITY_NAME, RARITY_ORDER } from '../../../data/machines';
import type { ScreenCtx } from '../../types';
import { affixLines } from '../../affixDescriptions';
import {
  GRIMOIRE_FACES_NODE,
  GRIMOIRE_NUMBERS_NODE,
  dieSpellsOf,
  grimoireLevelsOf,
  grimoireLine,
  slotSpellState,
  slotSpells,
  spellLabel,
  spellSidesText,
  spellTexts,
  type DieSpellDef,
} from '../../spellsApi';
import { materialSetTexts } from '../../materialsApi';
import {
  dieCardInnerHtml,
  formatGold,
  formatLevelFactor,
  formatNumber,
  formatPercent,
  formatSeconds,
  dieSidesLabel,
} from '../../format';
import { buildContainerPanel } from '../containerPanel';
import { icon } from '../../icons';
import { dieArt } from '../../diceArt';
import {
  DICE_SUBTABS,
  DICE_SUBTAB_LABEL,
  favoriteDice,
  groupInventoryByRarity,
  readDiceSubtab,
  readInventoryPanels,
  readRecyclePanels,
  writeDiceSubtab,
  writeInventoryPanel,
  writeRecyclePanel,
  type DiceSubtab,
  type RarityPanel,
} from '../../diceTabs';
import { buildSubtabNav } from './subtabs';
import { buildFavoriteButton, decorateDieCard, isFavorite } from '../../dieQuick';
import { buildDiceViewPanel, buildHiddenNotice } from './diceViewPanel';
import { filterDice, hasActiveFilter, presentMaterials, presentSides, readDiceView } from '../../diceView';
import type { RecycleFilter } from '../../../core/shop';
import { registerEscape } from '../../keyboard';
import { toast } from '../../toast';
import { equipRefusal } from '../../equipApi';
import { openEquipSlotModal } from './equipSlotModal';
import { slotTagText, slotTags, type SlotTag } from '../../rearguardApi';
import {
  dieLevelMaxOf,
  dieLevelOf,
  dieMaterialOf,
  enrichedFactor,
  corruptedFactor,
  forgeMarksOf,
  isCorruptedDie,
  isEnriched,
  levelFactorOf,
  materialView,
  dieLevelGrowth,
} from '../../abyssApi';

/**
 * Onglet « Dés » de l'Accalmie — COMMENTS §6 :
 *  1. une carte de dé se déplie au tap et détaille ses affixes (une seule à la
 *     fois, emplacements et inventaire confondus) ;
 *  2. une section « Sorts » liste les 4 combinaisons et leur état ;
 *  3. l'inventaire se triait par rareté, puissance ou date (retiré en A100) ;
 *  4. v0.2.3 (COMMENTS §3) : un recyclage en masse filtré par rareté et par
 *     nombre de faces, avec aperçu en direct, à côté du tri.
 *  5. v0.4.0 (COMMENTS v0.3.0 §1.5) : la section « Gobelet » en lecture seule —
 *     le contenant est entièrement dérivé de l'arbre, le sacrifice de dés
 *     Maîtres n'existe plus.
 *  6. v0.5.0 (COMMENTS v0.4.1 §1.4 / §2) : chaque dé affiche **ses** sorts (nom,
 *     condition, effet) et la section « Sorts » sépare les sorts
 *     d'emplacement (débloqués par `DerivedStats.slots`) des sorts de dés
 *     réellement présents dans l'inventaire.
 *  7. v0.15.0 (refonte F1, lot 2) : emplacements en **socles facettés**, cartes
 *     de dé F1 avec vignette SVG (`ui/diceArt.ts`), panneaux, tri et recycleur
 *     dans la charte (`.f-panel`, `.f-select`, `.f-btn`) — même information.
 *  8. v0.50.0 (COMMENTS v0.49.1 §2, A100-3) : quatre **sous-onglets**, le même
 *     composant que la Boutique (`subtabs.ts`), mémorisés pour la session :
 *     Équipés (emplacements puis Statistiques), Inventaire (dés non équipés en
 *     volets par rareté, `ui/diceTabs.ts`), Recycleur (en masse, puis à l'unité ;
 *     verrouillé sans le nœud) et Sorts. Le tri par rareté / puissance / date de
 *     l'inventaire disparaît : les volets le remplacent.
 */

/** `level` (v0.53.0, A103) : niveau décroissant, puis rareté, puis score — le tri par défaut. */
export type DiceSort = 'rarity' | 'power' | 'date' | 'level';

/**
 * Carte dépliée courante (identifiant de dé + origine). Vit au niveau du
 * module : l'onglet est reconstruit à chaque `refresh()` (achat, équipement…)
 * et le pli doit survivre à ces reconstructions.
 */
let expandedKey: string | null = null;

/**
 * Filtre du recyclage en masse (v0.55.8, A119 : plusieurs raretés et plusieurs
 * faces à la fois). Comme `expandedKey`, il vit au niveau du module pour
 * survivre à la reconstruction complète de l'onglet à chaque `refresh()`
 * (D.41 : aucune persistance). **Aucune pastille cochée = toutes** ; la case
 * « Épargner » est cochée à chaque chargement.
 */
const recycleRarities = new Set<Rarity>();
const recycleSidesSet = new Set<DieSides>();
let recycleSpare = true;

/** Raretés proposées au filtre (Prismatique depuis la GachaDice v20, v0.26.0). */
const RECYCLE_RARITIES: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'prismatic'];

/** Filtre courant, dans la forme attendue par `game.recycleWhere/previewRecycle`. */
function currentRecycleFilter(): RecycleFilter {
  return {
    rarities: [...recycleRarities],
    sidesList: [...recycleSidesSet],
    spareEnriched: recycleSpare,
    spareMaterial: recycleSpare,
  };
}

/**
 * Tri de l'inventaire. La fenêtre « Équiper l'emplacement N » trie par
 * `level` (v0.53.0, A103 : niveau décroissant — un dé de campagne vaut 0 —,
 * puis rareté décroissante, puis dégâts attendus ; `power` avant).
 * `date` s'appuie sur l'ordre d'insertion (les dés sont ajoutés à la fin par le
 * Gachapon) : le plus récent en premier. L'Inventaire de l'onglet Dés, lui, est
 * rangé en volets par rareté (`ui/diceTabs.ts`, même tri par niveau dans chaque volet).
 */
export function sortInventory(
  state: Readonly<GameState>,
  stats: DerivedStats,
  sort: DiceSort,
): MasterDie[] {
  const list = state.inventory.slice();
  if (sort === 'date') return list.reverse();
  if (sort === 'power') {
    return list.sort((a, b) => dieScore(b, stats) - dieScore(a, stats));
  }
  if (sort === 'level') {
    return list.sort((a, b) => {
      const level = dieLevelOf(b) - dieLevelOf(a);
      if (level !== 0) return level;
      const rarity = RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity);
      if (rarity !== 0) return rarity;
      return dieScore(b, stats) - dieScore(a, stats);
    });
  }
  // Rareté décroissante, puis dégâts attendus décroissants.
  return list.sort((a, b) => {
    const rarity = RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity);
    if (rarity !== 0) return rarity;
    return dieScore(b, stats) - dieScore(a, stats);
  });
}

/**
 * Panneau détaillé d'un dé : une ligne par affixe (nom, valeur, explication),
 * puis une ligne par **sort** du dé (nom, condition de déclenchement, effet) —
 * COMMENTS v0.4.1 §2.
 */
function buildAffixDetails(die: MasterDie, game: ScreenCtx['game']): HTMLElement {
  const details = document.createElement('div');
  details.className = 'die-details';

  // Mode infini (A94) : niveau, Matériau, Enrichi, Fusion — absents d'un dé de campagne.
  const infinite = buildInfiniteDetails(die, game);
  if (infinite) details.appendChild(infinite);

  const lines = affixLines(die.affixes, { die, game });
  const marks = forgeMarksOf(die);
  if (lines.length === 0) {
    const none = document.createElement('p');
    none.className = 'die-details-none';
    none.textContent = 'Ce dé ne porte aucun affixe : il ne fait que ses faces.';
    details.appendChild(none);
  } else {
    const list = document.createElement('dl');
    list.className = 'die-details-list';
    lines.forEach((line, i) => {
      const row = document.createElement('div');
      row.className = 'die-details-row';
      const tag =
        (marks.rerollSlot === i ? ' <span class="die-details-value" data-mark="reroll">relançable</span>' : '') +
        (marks.absolvedSlot === i ? ' <span class="die-details-value" data-mark="absolution">Absolution</span>' : '');
      const dt = document.createElement('dt');
      dt.innerHTML = `<span class="die-details-name">${line.name}</span> <span class="die-details-value">${line.value}</span>${tag}`;
      const dd = document.createElement('dd');
      dd.textContent = line.explanation;
      row.append(dt, dd);
      list.appendChild(row);
    });
    details.appendChild(list);
  }

  const spells = dieSpellsOf(die);
  if (spells.length > 0) {
    const list = document.createElement('dl');
    list.className = 'die-details-list die-details-spells';
    list.setAttribute('data-role', 'die-detail-spells');
    for (const spell of spells) {
      const row = document.createElement('div');
      row.className = 'die-details-row';
      row.setAttribute('data-spell', spell.id);
      const dt = document.createElement('dt');
      dt.innerHTML = `<span class="die-details-name">Sort — ${spell.name}</span>${
        spell.rare ? ' <span class="die-details-value die-spell-rare">rare</span>' : ''
      }`;
      const dd = document.createElement('dd');
      // A95 : condition et effet suivent les Grimoires (`game.spellDescription`).
      const texts = spellTexts(game, spell);
      // A98 : les sorts des gros dés disent à quels dés ils vont (« d12 et d20 », « d20 »).
      const sides = spellSidesText(spell);
      dd.textContent = [texts.condition, texts.description, sides ? `Sur les ${sides}` : '']
        .filter((t) => t && t !== '—')
        .join(' · ');
      row.append(dt, dd);
      list.appendChild(row);
    }
    details.appendChild(list);
  }
  return details;
}

/**
 * Lignes « mode infini » de la fiche d'un dé (A94) : niveau et facteur de
 * dégâts, Matériau et ses deux bonus, Enrichi, Fusion. `null` pour un dé de
 * campagne : sa fiche reste celle d'avant.
 */
function buildInfiniteDetails(die: MasterDie, game: ScreenCtx['game']): HTMLElement | null {
  const level = dieLevelOf(die);
  const material = dieMaterialOf(die);
  const enriched = isEnriched(die);
  const fused = forgeMarksOf(die).fused;
  const corrupted = isCorruptedDie(die);
  if (level === 0 && !material && !enriched && !fused && !corrupted) return null;

  const rows: [string, string, string][] = [];
  if (level > 0) {
    const max = dieLevelMaxOf(game);
    rows.push([
      `Niveau ${level}`,
      formatLevelFactor(levelFactorOf(game, die)),
      `La face obtenue vaut ${formatNumber(levelFactorOf(game, die), 2)} fois sa valeur (${formatNumber(dieLevelGrowth(game), 2)} par niveau).${
        max !== null ? ` Niveau maximum : ${max}.` : ''
      }`,
    ]);
  }
  if (material) {
    const view = materialView(material);
    const bonus = [view.bonus3 && `3 pièces : ${view.bonus3}`, view.bonus5 && `5 pièces : ${view.bonus5}`].filter(Boolean);
    // A95 : où en sont les pièces (dés Maîtres équipés) : « 3 pièces : actif » ou « 2 / 3 ».
    rows.push([
      `Matériau — ${view.name}`,
      materialSetTexts(game, material).join(' · '),
      bonus.length > 0 ? bonus.join(' · ') : 'Trois et cinq dés du même Matériau déclenchent un bonus.',
    ]);
  }
  if (corrupted) {
    // A98 : seuls les dés des Corrupted GachaDice se forgent ; leurs faces portent le Mult_Corrupted dans les dégâts plats (A109 : ×2 400 000).
    rows.push([
      'Corrupted',
      `×${formatGold(corruptedFactor(game))}`,
      `Dé des Corrupted GachaDice : ses faces comptent ${formatGold(corruptedFactor(game))} fois dans les dégâts plats, et lui seul se forge.`,
    ]);
  }
  if (enriched) {
    rows.push(['Enrichi', `×${formatNumber(enrichedFactor(), 1)}`, 'Les valeurs des affixes de ce dé sont multipliées.']);
  }
  if (fused) rows.push(['Fusionné', '', 'Ce dé est issu d’une Fusion : il ne peut plus être fusionné.']);

  const list = document.createElement('dl');
  list.className = 'die-details-list die-details-infinite';
  list.setAttribute('data-role', 'die-detail-infinite');
  for (const [name, value, text] of rows) {
    const row = document.createElement('div');
    row.className = 'die-details-row';
    const dt = document.createElement('dt');
    dt.innerHTML = `<span class="die-details-name">${name}</span>${value ? ` <span class="die-details-value">${value}</span>` : ''}`;
    const dd = document.createElement('dd');
    dd.textContent = text;
    row.append(dt, dd);
    list.appendChild(row);
  }
  return list;
}

/**
 * Rend une carte dépliable. `key` distingue la même arme en emplacement et en
 * inventaire (une seule carte ouverte à la fois, tous emplacements confondus).
 */
function makeExpandable(
  card: HTMLElement,
  die: MasterDie,
  key: string,
  refresh: () => void,
  game: ScreenCtx['game'],
): void {
  const open = expandedKey === key;
  card.classList.add('die-card--tappable');
  card.classList.toggle('die-card--expanded', open);
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('aria-expanded', open ? 'true' : 'false');
  card.dataset.dieKey = key;

  if (open) {
    card.appendChild(buildAffixDetails(die, game));
    // `Échap` replie la fiche (phase 2, A78) ; la carte reconstruite par
    // `refresh()` se réinscrit d'elle-même.
    registerEscape(card, () => {
      expandedKey = null;
      refresh();
    });
  }

  const toggle = (): void => {
    expandedKey = expandedKey === key ? null : key;
    refresh();
  };
  card.addEventListener('click', (e) => {
    // Les actions (Équiper, Recycler…) vivent dans la carte : elles ne doivent
    // pas replier/déplier au passage.
    if ((e.target as HTMLElement | null)?.closest('button')) return;
    toggle();
  });
  card.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if ((e.target as HTMLElement | null)?.closest('button')) return;
    e.preventDefault();
    toggle();
  });
}

function buildSlot(
  index: number,
  die: MasterDie | null,
  ctx: ScreenCtx,
  refresh: () => void,
  tags: readonly SlotTag[] = [],
): HTMLElement {
  const { game } = ctx;
  const slot = document.createElement('div');
  // Socle facetté (F1) : la pierre où le dé Maître est posé.
  slot.className = `die-slot f-cut${die ? '' : ' die-slot--empty'}`;
  slot.setAttribute('data-slot', String(index));
  const heading = document.createElement('h3');
  heading.className = 'die-slot-title';
  heading.textContent = `Emplacement ${index + 1}`;
  slot.appendChild(heading);

  if (!die) {
    const empty = document.createElement('p');
    empty.className = 'die-slot-empty';
    empty.innerHTML = `${icon('die')}<span>Vide</span><span class="die-slot-empty-hint">Toucher pour équiper</span>`;
    slot.appendChild(empty);
    // A88-8 : toucher l'emplacement vide ouvre la fenêtre « Équiper l'emplacement N ».
    slot.setAttribute('role', 'button');
    slot.setAttribute('tabindex', '0');
    slot.setAttribute('aria-label', `Équiper l'emplacement ${index + 1}`);
    const open = (): void => openEquipSlotModal(ctx, index, refresh);
    slot.addEventListener('click', open);
    slot.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      open();
    });
    return slot;
  }

  // A89 : sous-titre « Arrière-garde » (jamais sur un emplacement vide).
  if (tags.length > 0) {
    const tag = document.createElement('p');
    tag.className = 'die-slot-tag';
    tag.setAttribute('data-role', 'slot-tag');
    tag.dataset.tags = tags.join(' ');
    tag.textContent = slotTagText(tags);
    slot.appendChild(tag);
  }

  const card = document.createElement('div');
  card.className = 'die-card die-card--slot f-cut';
  // Rareté portée par l'élément : la CSS y accroche la couleur du cadre et des
  // puces d'affixe (style.css, « Dés »).
  card.dataset.rarity = die.rarity;
  card.innerHTML = dieCardInnerHtml(die, '', game);
  decorateDieCard(card, die, game, refresh, { temper: true });
  makeExpandable(card, die, `slot:${index}:${die.id}`, refresh, game);
  slot.appendChild(card);

  const unequip = document.createElement('button');
  unequip.type = 'button';
  unequip.className = 'f-btn f-btn--ghost f-btn--sm die-slot-remove';
  unequip.textContent = 'Retirer';
  unequip.addEventListener('click', () => {
    game.unequipDie(index);
    refresh();
  });
  slot.appendChild(unequip);

  return slot;
}

/**
 * Carte d'un dé de l'Inventaire (dé **non équipé**) : « Équiper » (un bouton par
 * emplacement, ou la rangée compacte au-delà de trois) et la fiche dépliable.
 * Le recyclage à l'unité vit dans le sous-onglet Recycleur (A100-3).
 */
function buildInventoryItem(die: MasterDie, ctx: ScreenCtx, refresh: () => void): HTMLElement {
  const { game } = ctx;
  const li = document.createElement('li');
  li.className = 'die-card die-card--inventory f-cut';
  li.dataset.rarity = die.rarity;
  li.dataset.dieId = die.id;
  li.innerHTML = dieCardInnerHtml(die, '', game);
  decorateDieCard(li, die, game, refresh, { temper: true });

  const actions = document.createElement('div');
  actions.className = 'die-card-actions';

  const slots = game.state.equipped.length;
  // v0.26.0 (A72-13) : jusqu'à 7 emplacements. Au-delà de trois, une rangée
  // compacte « Équiper : 1 2 3 … » remplace les longs boutons, qui
  // occuperaient sinon tout l'écran pour un seul dé.
  const compact = slots > 3;
  const host = compact ? document.createElement('div') : actions;
  if (compact) {
    host.className = 'die-equip-row';
    host.setAttribute('data-role', 'equip-row');
    const label = document.createElement('span');
    label.className = 'die-equip-label';
    label.textContent = 'Équiper :';
    host.appendChild(label);
  }
  for (let s = 0; s < slots; s++) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = compact ? 'f-btn f-btn--sm die-equip-slot' : 'f-btn f-btn--sm';
    btn.textContent = compact ? String(s + 1) : slots > 1 ? `Équiper (emplacement ${s + 1})` : 'Équiper';
    if (compact) btn.setAttribute('aria-label', `Équiper (emplacement ${s + 1})`);
    btn.setAttribute('data-equip-slot', String(s));
    btn.addEventListener('click', () => {
      // Un seul exemplaire de chaque dé nommé équipé à la fois (A72-14).
      const refusal = equipRefusal(game, die.id, s);
      if (refusal) {
        toast(refusal, 2600, 'lock');
        return;
      }
      game.equipDie(die.id, s);
      refresh();
    });
    host.appendChild(btn);
  }
  if (compact) actions.appendChild(host);

  li.appendChild(actions);
  makeExpandable(li, die, `inv:${die.id}`, refresh, game);
  return li;
}

function buildStatsPanel(ctx: ScreenCtx): HTMLElement {
  const { game } = ctx;
  const stats = game.getDerivedStats();
  const panel = document.createElement('section');
  panel.className = 'stats-panel f-panel';
  panel.innerHTML = `
    <h2 class="stats-title">${icon('die')}<span>Statistiques</span></h2>
    <dl class="stats-grid">
      <div><dt>Dégâts</dt><dd>${formatPercent(stats.damageMult - 1, { sign: true })}</dd></div>
      <div><dt>Critique</dt><dd>${formatPercent(stats.critChance)}</dd></div>
      <div><dt>Multiplicateur critique</dt><dd>×${stats.critMult.toFixed(2)}</dd></div>
      <div><dt>Temps de retour</dt><dd>${formatSeconds(stats.throwCooldown)}</dd></div>
      <div><dt>Durée d'expédition</dt><dd>${formatSeconds(stats.expeditionDuration, 0)}</dd></div>
      <div><dt>Durée d'invasion</dt><dd>${formatSeconds(stats.invasionDuration, 0)}</dd></div>
      <div><dt>Or</dt><dd>${formatPercent(stats.goldMult - 1, { sign: true })}</dd></div>
      <div><dt>Emplacements</dt><dd>${stats.slots}</dd></div>
      <div><dt>Puissance des sorts</dt><dd>×${stats.spellPower.toFixed(2).replace('.', ',')}</dd></div>
      <div class="stats-grid-wide"><dt>Sorts d'emplacement débloqués</dt><dd>${
        stats.unlockedSpells.length ? stats.unlockedSpells.map((id) => spellLabel(id)).join(' · ') : '—'
      }</dd></div>
    </dl>
  `;
  return panel;
}

/** « Dés concernés : d12 et d20 » — la ligne des sorts réservés aux gros dés (A98), vide pour les autres. */
function sidesLine(def: DieSpellDef): string {
  const sides = spellSidesText(def);
  return sides ? `<p class="spell-card-req" data-role="spell-sides">Dés concernés : ${sides}</p>` : '';
}

/**
 * Section « Sorts » — COMMENTS v0.4.1 §1.4.
 *
 *  a. les **sorts d'emplacement**, avec le nombre d'emplacements requis et leur
 *     état (débloqués dès que `DerivedStats.slots` l'atteint) ;
 *  b. les **sorts de dés** réellement présents dans l'inventaire, avec le
 *     nombre de dés qui les portent (un même sort ne se déclenche qu'une fois
 *     par lancer, DECISIONS §E A3) et un marqueur quand un dé équipé le porte.
 */
function buildSpellsPanel(ctx: ScreenCtx): HTMLElement {
  const { game } = ctx;
  const stats = game.getDerivedStats();
  const panel = document.createElement('section');
  panel.className = 'spells-panel f-panel';

  const slotCards = slotSpells()
    .map((s) => {
      // A95 : les sorts de Poker restent verrouillés tant que le nœud manque.
      const { unlocked, missingNode } = slotSpellState(game, s, stats.slots);
      const texts = spellTexts(game, s);
      return `
      <li class="spell-card ${unlocked ? 'spell-card--on' : 'spell-card--off'}" data-spell="${s.id}"${
        missingNode ? ' data-locked="node"' : ''
      }>
        <div class="spell-card-head">
          <span class="spell-card-name">${s.name}</span>
          <span class="spell-card-state">${unlocked ? `${icon('check')}Débloqué` : `${icon('lock')}Verrouillé`}</span>
        </div>
        <p class="spell-card-combo">${texts.condition}</p>
        <p class="spell-card-effect">${texts.description}</p>
        <p class="spell-card-req">${missingNode ? `Nécessite ${missingNode} · ` : ''}${s.requiredSlots} emplacements de dé Maître</p>
      </li>`;
    })
    .join('');

  // Grimoires : une ligne en tête de leur famille (nombres → sorts de dés,
  // Face je gagne → sorts d'emplacement), seulement une fois le nœud acquis.
  const grimoires = grimoireLevelsOf(game);
  const grimoireRow = (nodeId: string, level: number, role: string): string => {
    const line = grimoireLine(nodeId, level);
    return line ? `<p class="spells-grimoire" data-role="${role}">${icon('spell')}<span>${line}</span></p>` : '';
  };

  // Sorts de dés : comptés sur tout l'inventaire (équipés compris).
  const counts = new Map<string, { def: DieSpellDef; total: number; equipped: number }>();
  for (const die of game.state.inventory) {
    const isEquipped = game.state.equipped.includes(die.id);
    for (const spell of dieSpellsOf(die)) {
      const found = counts.get(spell.id) ?? { def: spell, total: 0, equipped: 0 };
      found.total += 1;
      if (isEquipped) found.equipped += 1;
      counts.set(spell.id, found);
    }
  }
  const dieCards = [...counts.values()]
    .sort((a, b) => b.equipped - a.equipped || b.total - a.total || a.def.name.localeCompare(b.def.name))
    .map(
      ({ def, total, equipped }) => `
      <li class="spell-card ${equipped > 0 ? 'spell-card--on' : 'spell-card--off'}" data-spell="${def.id}">
        <div class="spell-card-head">
          <span class="spell-card-name">${def.name}${def.rare ? ' <span class="die-spell-rare">rare</span>' : ''}</span>
          <span class="spell-card-state">${total} dé${total > 1 ? 's' : ''}${
            equipped > 0 ? ` · ${equipped} équipé${equipped > 1 ? 's' : ''}` : ''
          }</span>
        </div>
        <p class="spell-card-combo">${spellTexts(game, def).condition}</p>
        <p class="spell-card-effect">${spellTexts(game, def).description}</p>
        ${sidesLine(def)}
      </li>`,
    )
    .join('');

  panel.innerHTML = `
    <h2 class="stats-title">${icon('spell')}<span>Sorts</span></h2>
    <h3 class="spells-subtitle">Sorts d'emplacement</h3>
    ${grimoireRow(GRIMOIRE_FACES_NODE, grimoires.faces, 'grimoire-faces')}
    <ul class="spell-list" data-role="slot-spells">${slotCards}</ul>
    <h3 class="spells-subtitle">Sorts de dés</h3>
    ${grimoireRow(GRIMOIRE_NUMBERS_NODE, grimoires.numbers, 'grimoire-numbers')}
    ${
      dieCards
        ? `<ul class="spell-list" data-role="die-spells">${dieCards}</ul>`
        : '<p class="spells-note" data-role="die-spells-empty">Aucun dé de votre inventaire ne porte de sort.</p>'
    }
    <p class="spells-note">Un sort d'emplacement et les sorts de dés distincts peuvent se déclencher sur le même lancer.</p>
  `;
  return panel;
}

/**
 * Recyclage en masse filtré — COMMENTS v0.2.3 §3.
 * Renvoie `null` tant que le nœud *Recycleur* n'est pas acheté : la section
 * n'existe alors pas du tout dans le DOM (`game.canRecycle()`).
 */
function buildRecycleFilter(ctx: ScreenCtx, refresh: () => void): HTMLElement | null {
  const { game } = ctx;
  if (!game.canRecycle()) return null;

  const section = document.createElement('section');
  section.className = 'recycle-filter f-panel';
  section.setAttribute('data-role', 'recycle-filter');

  const title = document.createElement('h2');
  title.className = 'inventory-title';
  title.textContent = 'Recycleur';
  section.appendChild(title);

  // Faces présentes dans l'inventaire, plus celles déjà cochées (une face recyclée jusqu'au dernier
  // dé garde sa pastille : le filtre reste lisible et se décoche d'un toucher).
  const sidesPresent = [...new Set([...presentSides(game.state.inventory), ...recycleSidesSet])].sort((a, b) => a - b);

  const chip = (role: string, attr: 'rarity' | 'sides', value: string, label: string, on: boolean): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'recycle-chip';
    b.setAttribute('data-role', role);
    b.dataset[attr] = value;
    b.setAttribute('aria-pressed', String(on));
    b.textContent = label;
    return b;
  };
  const group = (label: string, role: string): [HTMLElement, HTMLElement] => {
    const wrap = document.createElement('div');
    wrap.className = 'recycle-chip-group';
    const text = document.createElement('span');
    text.className = 'recycle-chip-label';
    text.textContent = label;
    const row = document.createElement('div');
    row.className = 'recycle-chips';
    row.setAttribute('data-role', role);
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', label);
    wrap.append(text, row);
    return [wrap, row];
  };
  const [rarityGroup, rarityRow] = group('Raretés', 'recycle-rarities');
  for (const r of RECYCLE_RARITIES) {
    const c = chip('recycle-rarity-chip', 'rarity', r, RARITY_NAME[r], recycleRarities.has(r));
    c.classList.add('recycle-chip--rarity');
    rarityRow.appendChild(c);
  }
  const [sidesGroup, sidesRow] = group('Faces', 'recycle-sides-list');
  for (const n of sidesPresent) sidesRow.appendChild(chip('recycle-sides-chip', 'sides', String(n), `d${n}`, recycleSidesSet.has(n)));
  section.append(rarityGroup, sidesGroup);

  const spareLabel = document.createElement('label');
  spareLabel.className = 'recycle-spare';
  const spareBox = document.createElement('input');
  spareBox.type = 'checkbox';
  spareBox.className = 'f-check';
  spareBox.setAttribute('data-role', 'recycle-spare');
  spareBox.checked = recycleSpare;
  const spareText = document.createElement('span');
  spareText.textContent = 'Épargner les dés Enrichis et à Matériau';
  spareLabel.append(spareBox, spareText);
  section.appendChild(spareLabel);

  const scope = document.createElement('p');
  scope.className = 'recycle-filter-scope';
  scope.setAttribute('data-role', 'recycle-scope');
  section.appendChild(scope);

  const preview = document.createElement('p');
  preview.className = 'recycle-filter-preview';
  preview.setAttribute('data-role', 'recycle-preview');
  section.appendChild(preview);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'f-btn f-btn--danger f-btn--block';
  button.setAttribute('data-role', 'recycle-run');
  button.textContent = 'Recycler';
  section.appendChild(button);

  const note = document.createElement('p');
  note.className = 'recycle-filter-note';
  note.textContent = 'Les dés équipés ne sont jamais recyclés.';
  section.appendChild(note);

  /** Aperçu en direct : aucun état de jeu n'est modifié (`previewRecycle`). */
  const update = (): void => {
    const rarities = RECYCLE_RARITIES.filter((r) => recycleRarities.has(r)).map((r) => RARITY_NAME[r]);
    const sides = sidesPresent.filter((n) => recycleSidesSet.has(n)).map((n) => `d${n}`);
    scope.textContent =
      `Raretés : ${rarities.length > 0 ? rarities.join(', ') : 'Toutes'} · Faces : ${sides.length > 0 ? sides.join(', ') : 'Toutes'}` +
      (recycleSpare ? ' · Enrichis et à Matériau épargnés' : '') +
      ' · favoris épargnés';
    const { count, gold } = game.previewRecycle(currentRecycleFilter());
    preview.textContent = `${count} dé${count > 1 ? 's' : ''} → +${formatGold(gold)} or`;
    button.disabled = count === 0;
  };
  update();

  section.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>('.recycle-chip');
    if (!el) return;
    const on = el.getAttribute('aria-pressed') !== 'true';
    el.setAttribute('aria-pressed', String(on));
    if (el.dataset.rarity) {
      const r = el.dataset.rarity as Rarity;
      if (on) recycleRarities.add(r);
      else recycleRarities.delete(r);
    } else if (el.dataset.sides) {
      const n = Number(el.dataset.sides) as DieSides;
      if (on) recycleSidesSet.add(n);
      else recycleSidesSet.delete(n);
    }
    update();
  });
  spareBox.addEventListener('change', () => {
    recycleSpare = spareBox.checked;
    update();
  });

  button.addEventListener('click', () => {
    const filter = currentRecycleFilter();
    const { count } = game.previewRecycle(filter);
    if (count === 0) return;
    // Au-delà de 10 dés, une erreur de filtre coûte cher : on confirme.
    if (count >= 10 && !window.confirm(`Recycler ${count} dés ? Cette action est définitive.`)) {
      return;
    }
    game.recycleWhere(filter);
    refresh();
  });

  return section;
}

/**
 * Un volet `<details>` dévoilable (Inventaire et recyclage à l'unité). L'état
 * choisi par le joueur est mémorisé pour la session ; l'ouverture posée au rendu
 * (par défaut ou mémorisée) n'est jamais réécrite — `toggle` ne mémorise que ce
 * qui s'en écarte.
 */
function buildRarityPanel(
  panel: RarityPanel,
  role: string,
  remember: (rarity: Rarity, open: boolean) => void,
  summaryExtra = '',
): HTMLDetailsElement {
  const details = document.createElement('details');
  details.className = 'f-panel f-details rarity-panel';
  details.setAttribute('data-role', role);
  details.dataset.rarity = panel.rarity;
  details.open = panel.open;
  const summary = document.createElement('summary');
  summary.className = 'rarity-panel-title';
  summary.innerHTML = `<span class="rarity-panel-name" data-rarity="${panel.rarity}">${panel.title}</span>${summaryExtra}`;
  details.appendChild(summary);
  details.addEventListener('toggle', () => {
    if (details.open !== panel.open) remember(panel.rarity, details.open);
  });
  return details;
}

/** État d'ouverture du volet « Favoris » (ouvert par défaut), gardé d'une reconstruction à l'autre. */
const favoritesOpen: Record<'inventory' | 'recycler', boolean> = { inventory: true, recycler: true };

/**
 * Volet « Favoris » (A122-8), **en tête et ouvert** : `[data-panel="favorites"]`.
 * Renvoie le volet et sa liste, à remplir avec les cartes (Inventaire) ou les lignes (Recycleur).
 */
function buildFavoritesPanel(count: number, where: 'inventory' | 'recycler'): { details: HTMLDetailsElement; list: HTMLUListElement } {
  const details = document.createElement('details');
  details.className = 'f-panel f-details rarity-panel favorites-panel';
  details.setAttribute('data-panel', 'favorites');
  details.setAttribute('data-role', where === 'inventory' ? 'favorites-panel' : 'recycle-favorites-panel');
  details.open = favoritesOpen[where];
  const summary = document.createElement('summary');
  summary.className = 'rarity-panel-title';
  summary.innerHTML = `<span class="rarity-panel-name favorites-panel-name">★ Favoris · ${count}</span>`;
  details.appendChild(summary);
  details.addEventListener('toggle', () => {
    favoritesOpen[where] = details.open;
  });
  const list = document.createElement('ul');
  list.className = where === 'inventory' ? 'inventory-list' : 'recycle-unit-list';
  details.appendChild(list);
  return { details, list };
}

/**
 * Panneau « Tri et filtres » (A119) pour la liste des dés non équipés, suivi de
 * la ligne « N dés masqués par les filtres — Tout afficher » quand un filtre en
 * cache. Le même composant sert l'Inventaire et le Recycleur.
 */
function buildViewHead(ctx: ScreenCtx, refresh: () => void): HTMLElement[] {
  const { game } = ctx;
  const view = readDiceView();
  const equipped = new Set(game.state.equipped.filter((id): id is string => typeof id === 'string'));
  const spare = game.state.inventory.filter((d) => !equipped.has(d.id));
  const hidden = hasActiveFilter(view) ? spare.length - filterDice(spare, view).length : 0;
  const nodes: HTMLElement[] = [
    buildDiceViewPanel({
      onChange: refresh,
      materials: presentMaterials(game.state.inventory),
      sides: presentSides(game.state.inventory),
    }),
  ];
  const notice = buildHiddenNotice(hidden, refresh);
  if (notice) nodes.push(notice);
  return nodes;
}

/** Sous-onglet « Inventaire » : les dés non équipés, en volets par rareté (A100-3). */
function buildInventoryPanels(ctx: ScreenCtx, refresh: () => void): HTMLElement {
  const { game } = ctx;
  const stats = game.getDerivedStats();
  const equipped = new Set(game.state.equipped.filter((id): id is string => typeof id === 'string'));
  const view = readDiceView();
  const panels = groupInventoryByRarity(game.state.inventory, equipped, (d) => dieScore(d, stats), readInventoryPanels(), true, view);
  const favorites = favoriteDice(game.state.inventory, equipped, (d) => dieScore(d, stats), view);

  const section = document.createElement('section');
  section.className = 'inventory-panels';
  section.setAttribute('data-role', 'inventory-panels');
  const hasSpare = game.state.inventory.some((d) => !equipped.has(d.id));
  if (!hasSpare) {
    const none = document.createElement('p');
    none.className = 'spells-note';
    none.setAttribute('data-role', 'inventory-empty');
    none.textContent = 'Aucun dé en réserve : les dés équipés sont dans l’onglet Équipés, les nouveaux se fabriquent à la Boutique.';
    section.appendChild(none);
    return section;
  }
  section.append(...buildViewHead(ctx, refresh));
  if (panels.length === 0 && favorites.length === 0) {
    const none = document.createElement('p');
    none.className = 'spells-note';
    none.setAttribute('data-role', 'inventory-filtered-empty');
    none.textContent = 'Aucun dé ne correspond aux filtres.';
    section.appendChild(none);
    return section;
  }
  if (favorites.length > 0) {
    const { details, list } = buildFavoritesPanel(favorites.length, 'inventory');
    for (const die of favorites) list.appendChild(buildInventoryItem(die, ctx, refresh));
    section.appendChild(details);
  }
  for (const panel of panels) {
    const details = buildRarityPanel(panel, 'rarity-panel', writeInventoryPanel);
    const list = document.createElement('ul');
    list.className = 'inventory-list';
    for (const die of panel.dice) list.appendChild(buildInventoryItem(die, ctx, refresh));
    details.appendChild(list);
    section.appendChild(details);
  }
  return section;
}

/** Ligne du recyclage à l'unité : un favori a son bouton désactivé, « Favori » (A122-8, protection du cœur). */
function buildRecycleRow(die: MasterDie, game: ScreenCtx['game'], refresh: () => void): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'recycle-row';
  li.setAttribute('data-role', 'recycle-unit');
  li.dataset.dieId = die.id;
  li.dataset.rarity = die.rarity;
  const art = document.createElement('span');
  art.className = 'recycle-row-art';
  art.innerHTML = dieArt(die.sides, die.rarity, { size: 28 });
  const name = document.createElement('span');
  name.className = 'recycle-row-name';
  name.innerHTML = `<b>${die.name}</b> <span class="recycle-row-sides">${dieSidesLabel(die.sides)}</span>`;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'f-btn f-btn--ghost f-btn--sm recycle-row-btn';
  btn.setAttribute('data-action', 'recycle-die');
  if (isFavorite(die)) {
    btn.disabled = true;
    btn.textContent = 'Favori';
    btn.title = 'Un favori n’est jamais recyclé : retirez l’étoile pour le recycler.';
  } else {
    btn.textContent = `Recycler (+${formatGold(recycleValue(die))} or)`;
    btn.addEventListener('click', () => {
      game.recycleDie(die.id);
      refresh();
    });
  }
  const actions = document.createElement('span');
  actions.className = 'recycle-row-actions';
  actions.append(buildFavoriteButton(die, game, refresh), btn);
  li.append(art, name, actions);
  return li;
}

/**
 * Recyclage à l'unité : les dés non équipés en volets par rareté (fermés tant
 * que le joueur ne les ouvre pas), une ligne compacte et « Recycler (+X or) »
 * par dé. Le titre d'un volet dit ce que rendrait tout le volet. A122-8 : le
 * volet « Favoris » est en tête et ouvert ; les favoris ne se recyclent pas.
 */
function buildRecycleUnits(ctx: ScreenCtx, refresh: () => void): HTMLElement {
  const { game } = ctx;
  const stats = game.getDerivedStats();
  const equipped = new Set(game.state.equipped.filter((id): id is string => typeof id === 'string'));
  const view = readDiceView();
  // Ici « fermé par défaut » : un volet n'est ouvert que si le joueur l'a ouvert.
  const panels = groupInventoryByRarity(game.state.inventory, equipped, (d) => dieScore(d, stats), readRecyclePanels(), false, view);
  const favorites = favoriteDice(game.state.inventory, equipped, (d) => dieScore(d, stats), view);
  const hasSpare = game.state.inventory.some((d) => !equipped.has(d.id));

  const section = document.createElement('section');
  section.className = 'recycle-units f-panel';
  section.setAttribute('data-role', 'recycle-units');
  const title = document.createElement('h2');
  title.className = 'inventory-title';
  title.textContent = 'À l’unité';
  section.appendChild(title);
  if (panels.length === 0 && favorites.length === 0) {
    const none = document.createElement('p');
    none.className = 'spells-note';
    none.setAttribute('data-role', hasSpare ? 'recycle-units-filtered-empty' : 'recycle-units-empty');
    none.textContent = hasSpare
      ? 'Aucun dé ne correspond aux filtres.'
      : 'Aucun dé à recycler : les dés équipés ne le sont jamais.';
    section.appendChild(none);
    return section;
  }
  if (favorites.length > 0) {
    const { details, list } = buildFavoritesPanel(favorites.length, 'recycler');
    for (const die of favorites) list.appendChild(buildRecycleRow(die, game, refresh));
    section.appendChild(details);
  }
  for (const panel of panels) {
    const total = panel.dice.reduce((sum, d) => sum + recycleValue(d), 0);
    const details = buildRarityPanel(
      panel,
      'recycle-panel',
      writeRecyclePanel,
      `<span class="rarity-panel-gold">+${formatGold(total)} or</span>`,
    );
    const list = document.createElement('ul');
    list.className = 'recycle-unit-list';
    for (const die of panel.dice) list.appendChild(buildRecycleRow(die, game, refresh));
    details.appendChild(list);
    section.appendChild(details);
  }
  return section;
}

/** Sous-onglet « Recycleur » : verrouillé sans le nœud, sinon recyclage en masse puis à l'unité. */
function buildRecyclerTab(ctx: ScreenCtx, refresh: () => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'recycler-tab';
  const filter = buildRecycleFilter(ctx, refresh);
  if (!filter) {
    const locked = document.createElement('p');
    locked.className = 'recycler-locked f-panel';
    locked.setAttribute('data-role', 'recycler-locked');
    locked.innerHTML = `${icon('lock')}<span>Débloquez le Recycleur dans l’Arbre</span>`;
    wrap.appendChild(locked);
    return wrap;
  }
  // A119 : le panneau « Tri et filtres » s'insère entre la section en masse et les volets à l'unité.
  const equipped = new Set(ctx.game.state.equipped.filter((id): id is string => typeof id === 'string'));
  if (ctx.game.state.inventory.some((d) => !equipped.has(d.id))) wrap.append(filter, ...buildViewHead(ctx, refresh), buildRecycleUnits(ctx, refresh));
  else wrap.append(filter, buildRecycleUnits(ctx, refresh));
  return wrap;
}

/** Sous-onglet « Équipés » : les emplacements, puis les Statistiques (A100-3), puis le Gobelet s'il existe. */
function buildEquippedTab(ctx: ScreenCtx, refresh: () => void): HTMLElement {
  const { game } = ctx;
  const wrap = document.createElement('div');
  wrap.className = 'equipped-tab';
  const slots = document.createElement('div');
  slots.className = 'die-slots';
  const equipped = game.state.equipped;
  // Recalculés à chaque reconstruction de l'onglet, donc à chaque équipement / retrait.
  const tagsBySlot = slotTags(game);
  for (let i = 0; i < equipped.length; i++) {
    const id = equipped[i];
    const die = id ? (game.state.inventory.find((d) => d.id === id) ?? null) : null;
    slots.appendChild(buildSlot(i, die, ctx, refresh, tagsBySlot.get(i)));
  }
  wrap.appendChild(slots);
  wrap.appendChild(buildStatsPanel(ctx));
  // Gobelet (COMMENTS v0.3.0 §1.5) : absent du DOM tant qu'il n'y a pas de
  // contenant, et purement informatif depuis la refonte du prestige.
  const goblet = buildContainerPanel(ctx);
  if (goblet) wrap.appendChild(goblet);
  return wrap;
}

export function renderDiceTab(container: HTMLElement, ctx: ScreenCtx, refresh: () => void): void {
  const wrap = document.createElement('div');
  wrap.className = 'dice-tab';
  const active = readDiceSubtab();
  wrap.setAttribute('data-subtab-active', active);

  wrap.appendChild(
    buildSubtabNav<DiceSubtab>({
      tabs: DICE_SUBTABS,
      labels: DICE_SUBTAB_LABEL,
      active,
      ariaLabel: 'Dés',
      navRole: 'dice-subtabs',
      tabRole: 'dice-subtab',
      navClass: 'dice-subtabs',
      tabClass: 'dice-subtab',
      onPick: (tab) => {
        writeDiceSubtab(tab);
        // Seul l'onglet Dés se redessine : le défilement repart du haut.
        container.replaceChildren();
        renderDiceTab(container, ctx, refresh);
        container.scrollTop = 0;
        container.querySelector<HTMLElement>(`[data-role="dice-subtab"][data-subtab="${tab}"]`)?.focus({ preventScroll: true });
      },
    }),
  );

  if (active === 'equipped') wrap.appendChild(buildEquippedTab(ctx, refresh));
  else if (active === 'inventory') wrap.appendChild(buildInventoryPanels(ctx, refresh));
  else if (active === 'recycler') wrap.appendChild(buildRecyclerTab(ctx, refresh));
  else wrap.appendChild(buildSpellsPanel(ctx));

  container.appendChild(wrap);
}
